import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bountyService, uploadService } from '../api/services';
import BlockRenderer from '../components/BlockRenderer';
import BountyPeoplePicker from '../components/BountyPeoplePicker';
import BountyAllocationModal from '../components/BountyAllocationModal';
import { typeLabel, levelLabel, statusLabel, statusClass, levelClass } from '../utils/bountyLabels';
import './Bounty.css';

const BountyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bounty, setBounty] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);
  const [allocOpen, setAllocOpen] = useState(false);
  // 通用確認彈窗（領取/完成/結案/退回/刪除等需二次確認的操作）
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await bountyService.get(id);
      setBounty(res.data);
      // 載入此懸賞的附件（失敗不阻斷詳情顯示）；url 補成絕對路徑供圖片預覽與下載
      try {
        const attachRes = await uploadService.listByBounty(id);
        setAttachments((attachRes.data || []).map((a) => ({ ...a, url: uploadService.getFileUrl(`/api/uploads/file/${a.file_path}`) })));
      } catch { setAttachments([]); }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // 統一執行操作：執行中鎖定按鈕，完成後重新載入詳情（或導頁）
  const runAction = async (fn, { nav } = {}) => {
    setBusy(true);
    setActionError('');
    try {
      await fn();
      setConfirm(null);
      if (nav) navigate(nav); else await load();
    } catch (err) {
      setActionError(err.message);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="container mt-5">載入中...</div>;
  if (error) return <div className="container mt-5"><div className="alert alert-danger">{error}</div></div>;
  if (!bounty) return null;

  const { can = {}, owner, collaborators = [], viewer = {}, settings = {} } = bounty;
  const pointsEnabled = settings.pointsEnabled;
  const excludeIds = [owner?.id, ...collaborators.map((c) => c.id)].filter(Boolean);

  // 領取二次確認：依退回開關提示「領取後是否可退回」
  const openClaimConfirm = () => setConfirm({
    title: '確定領取這個懸賞？',
    message: settings.returnEnabled
      ? '領取後你將成為負責人。目前已開啟退回功能，必要時仍可退回。'
      : '領取後你將成為負責人，且目前無法自行退回，請確認後再領取。',
    confirmLabel: '確定領取',
    onConfirm: () => runAction(() => bountyService.claim(id)),
  });

  const metaRows = [
    ['分類', typeLabel(bounty.bounty_type)],
    ['等級', levelLabel(bounty.level)],
    ['截止期限', bounty.deadline || '未定'],
    ['預估工時', bounty.estimated_hours != null ? `${bounty.estimated_hours} 小時` : '—'],
    ['提出人', bounty.proposer_name || '—'],
    ['開立者', bounty.author_name],
  ];
  if (pointsEnabled) metaRows.push(['點數', `${bounty.points} 點`]);
  if (bounty.address) metaRows.push(['地址', bounty.address]);
  if (bounty.contact) metaRows.push(['聯絡窗口', bounty.contact]);
  if (bounty.assignee_name) metaRows.push(['指派負責人', bounty.assignee_name]);

  return (
    <div className="bounty-detail">
      <div className="container">
        <button type="button" className="bounty-back" onClick={() => navigate('/bounties')}>← 返回懸賞榜</button>

        <div className="bounty-detail-head">
          <div className="bounty-detail-badges">
            <span className={`badge badge-type-${String(bounty.bounty_type).toLowerCase()}`}>{typeLabel(bounty.bounty_type)}</span>
            <span className={`badge ${levelClass(bounty.level)}`}>等級 {levelLabel(bounty.level)}</span>
            <span className={`badge ${statusClass(bounty.status)}`}>{statusLabel(bounty.status)}</span>
          </div>
          <h1>{bounty.title}</h1>
        </div>

        {actionError && <div className="alert alert-danger">{actionError}</div>}

        <div className="bounty-detail-grid">
          <div className="bounty-detail-main">
            <section className="bounty-section">
              <h3>問題描述 / 任務範圍</h3>
              <BlockRenderer content={bounty.content} attachments={attachments} />
            </section>
            {bounty.remark && (
              <section className="bounty-section">
                <h3>備註描述</h3>
                <BlockRenderer content={bounty.remark} />
              </section>
            )}
          </div>

          <aside className="bounty-detail-side">
            <div className="bounty-info-card">
              <h3>懸賞資訊</h3>
              <dl className="bounty-meta-list">
                {metaRows.map(([k, v]) => (
                  <div key={k} className="bounty-meta-row"><dt>{k}</dt><dd>{v}</dd></div>
                ))}
              </dl>
            </div>

            <div className="bounty-info-card">
              <h3>領取狀態</h3>
              <div className="bounty-owner-line">
                <strong>負責人：</strong>{owner ? owner.name : <span className="muted">尚無人領取</span>}
                {viewer.isOwner && <span className="badge badge-me">我</span>}
              </div>
              <div className="bounty-collab-block">
                <strong>協作者：</strong>
                {collaborators.length === 0 ? <span className="muted"> 無</span> : (
                  <ul className="bounty-collab-list">
                    {collaborators.map((c) => (
                      <li key={c.id}>
                        {c.name}
                        {can.assign && (
                          <button type="button" className="collab-remove" disabled={busy}
                            onClick={() => runAction(() => bountyService.removeCollaborator(id, c.id))} title="移除協作者">✕</button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="bounty-actions">
              {can.claim && <button className="btn btn-accent" disabled={busy} onClick={openClaimConfirm}>領取</button>}
              {can.assign && <button className="btn btn-primary" disabled={busy} onClick={() => setTransferOpen(true)}>轉派</button>}
              {can.assign && <button className="btn btn-primary" disabled={busy} onClick={() => setCollabOpen(true)}>新增協作者</button>}
              {can.allocate && <button className="btn btn-primary" disabled={busy} onClick={() => setAllocOpen(true)}>分配點數</button>}
              {can.complete && (
                <button className="btn btn-primary" disabled={busy}
                  onClick={() => setConfirm({ title: '標記完成？', message: '標記完成後將通知開立者確認結案。', confirmLabel: '標記完成', onConfirm: () => runAction(() => bountyService.complete(id)) })}>標記完成</button>
              )}
              {can.withdrawCompletion && (
                <button className="btn btn-warning" disabled={busy}
                  onClick={() => setConfirm({ title: '收回結案申請？', message: '收回後此懸賞將回到進行中，你仍是負責人，可再次標記完成。', confirmLabel: '確定收回', onConfirm: () => runAction(() => bountyService.uncomplete(id)) })}>收回結案申請</button>
              )}
              {can.rejectCompletion && (
                <button className="btn btn-warning" disabled={busy}
                  onClick={() => setConfirm({ title: '退回結案？', message: '退回後此懸賞將回到進行中，負責人仍維持不變，請其繼續處理後再提交。', confirmLabel: '確定退回', onConfirm: () => runAction(() => bountyService.uncomplete(id)) })}>退回結案</button>
              )}
              {can.close && (
                <button className="btn btn-success" disabled={busy}
                  onClick={() => setConfirm({ title: '確定結案？', message: pointsEnabled ? '結案後將依「分配點數」把點數發給各參與者（未分配則全給負責人），且無法復原。' : '結案後此懸賞將標記為已完成，無法復原。', confirmLabel: '確定結案', onConfirm: () => runAction(() => bountyService.close(id)) })}>結案</button>
              )}
              {can.return && (
                <button className="btn btn-warning" disabled={busy}
                  onClick={() => setConfirm({ title: '確定退回？', message: '退回後此懸賞將回到開放中，所有領取與協作關係會被解除。', confirmLabel: '確定退回', onConfirm: () => runAction(() => bountyService.giveBack(id)) })}>退回</button>
              )}
              {can.edit && <button className="btn btn-secondary" disabled={busy} onClick={() => navigate(`/bounties/${id}/edit`)}>編輯</button>}
              {can.remove && (
                <button className="btn btn-danger" disabled={busy}
                  onClick={() => setConfirm({ title: '確定刪除？', message: '刪除後將無法復原。', confirmLabel: '確定刪除', onConfirm: () => runAction(() => bountyService.delete(id), { nav: '/bounties' }) })}>刪除</button>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* 轉派：單選一位新負責人 */}
      <BountyPeoplePicker
        open={transferOpen}
        mode="single"
        title="轉派給"
        confirmLabel="確定轉派"
        excludeIds={excludeIds}
        onClose={() => setTransferOpen(false)}
        onConfirm={(users) => { setTransferOpen(false); runAction(() => bountyService.transfer(id, users[0].id)); }}
      />

      {/* 協作：可多選 */}
      <BountyPeoplePicker
        open={collabOpen}
        mode="multi"
        title="新增協作人員"
        confirmLabel="加入協作"
        excludeIds={excludeIds}
        onClose={() => setCollabOpen(false)}
        onConfirm={(users) => { setCollabOpen(false); runAction(() => bountyService.addCollaborators(id, users.map((u) => u.id))); }}
      />

      {/* 點數分配：負責人/管理者把懸賞點數分給自己與協作者 */}
      <BountyAllocationModal
        bountyId={id}
        open={allocOpen}
        onClose={() => setAllocOpen(false)}
        onSaved={load}
      />

      {/* 通用二次確認彈窗 */}
      {confirm && (
        <div className="bounty-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="bounty-modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bounty-modal-header"><h3>{confirm.title}</h3></div>
            <p className="confirm-message">{confirm.message}</p>
            <div className="bounty-modal-footer">
              <span />
              <div>
                <button type="button" className="btn btn-secondary" onClick={() => setConfirm(null)} disabled={busy}>取消</button>
                <button type="button" className="btn btn-primary" onClick={confirm.onConfirm} disabled={busy}>{confirm.confirmLabel || '確定'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BountyDetailPage;
